import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { MobileBottomNav } from './MobileBottomNav';
import { useAuth } from '../../store/authStore';
import { Button, Badge } from '../ui';
import { Icon } from '@/design-system/assets/icons/Icon.jsx';
import { toast } from '../../utils/toast';
import { noticesApiService } from '../../services/noticesApiService';
import { scheduleApiService } from '../../services/scheduleApiService';

interface LayoutProps {
  children: React.ReactNode;
  sidebarSections?: Array<{
    id: string;
    title: string;
    items: Array<{
      id: string;
      label: string;
      icon: React.ReactNode;
      href: string;
      badge?: string | number;
      children?: Array<{
        id: string;
        label: string;
        icon: React.ReactNode;
        href: string;
        badge?: string | number;
      }>;
    }>;
  }>;
  headerActions?: React.ReactNode;
  className?: string;
  sidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  maxWidth?: string;
}

export const Layout: React.FC<LayoutProps> = ({
  children,
  sidebarSections = [],
  headerActions,
  className,
  sidebarCollapsed: controlledCollapsed,
  onSidebarToggle,
  maxWidth = 'max-w-[1600px]',
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [todayNoticesCount, setTodayNoticesCount] = useState<number>(0);
  const [todaySchedulesCount, setTodaySchedulesCount] = useState<number>(0);
  const { user, logout, userName } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const navigate = useNavigate();

  const isCollapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;

  // 컴포넌트 마운트 시 오늘 특이사항 및 일정 개수 로드
  useEffect(() => {
    loadTodayNoticesCount();
    loadTodaySchedulesCount();

    // 특이사항 변경 이벤트 리스너 추가
    const handleNoticesChange = () => {
      loadTodayNoticesCount();
    };

    // 일정 변경 이벤트 리스너 추가
    const handleSchedulesChange = () => {
      loadTodaySchedulesCount();
    };

    window.addEventListener('notices-changed', handleNoticesChange);
    window.addEventListener('schedules-changed', handleSchedulesChange);

    return () => {
      window.removeEventListener('notices-changed', handleNoticesChange);
      window.removeEventListener('schedules-changed', handleSchedulesChange);
    };
  }, []);

  const handleToggleCollapse = () => {
    if (onSidebarToggle) {
      onSidebarToggle(!isCollapsed);
    } else {
      setInternalCollapsed(!isCollapsed);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('로그아웃 되었습니다.');
    navigate('/login');
  };

  // 오늘 특이사항 개수 가져오기
  const loadTodayNoticesCount = async () => {
    try {
      const count = await noticesApiService.getTodayNoticesCount();
      setTodayNoticesCount(count);
    } catch (error) {
      console.error('오늘 특이사항 개수 로드 실패:', error);
      setTodayNoticesCount(0);
    }
  };

  // 오늘 일정 개수 가져오기
  const loadTodaySchedulesCount = async () => {
    try {
      const count = await scheduleApiService.getTodaySchedulesCount();
      setTodaySchedulesCount(count);
    } catch (error) {
      console.error('오늘 일정 개수 로드 실패:', error);
      setTodaySchedulesCount(0);
    }
  };

  // 기본 헤더 액션 (사용자 정보 + 설정 + 로그아웃)
  const defaultHeaderActions = (
    <div className="flex items-center gap-2 lg:gap-3">
      {/* 사용자 정보 */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        <Icon name="Person" size={16} className="text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground max-w-[88px] sm:max-w-none truncate">{userName}</span>
        <Badge
          variant={user?.role === 'ADMIN' ? 'destructive' : user?.role === 'STAFF' ? 'secondary' : 'default'}
          className="hidden sm:inline-flex text-[10px] px-1.5 py-0.5 h-auto font-normal"
        >
          {user?.role === 'ADMIN' ? '관리자' : user?.role === 'STAFF' ? '직원' : '사용자'}
        </Badge>
      </div>

      {/* 로그아웃 버튼 (모바일에서는 하단 전체메뉴로 이동) */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLogout}
        className="hidden lg:inline-flex p-2 hover:bg-destructive hover:text-destructive-foreground transition-colors"
        title="로그아웃"
      >
        <Icon name="LogOut" size={16} />
      </Button>
    </div>
  );

  // 사용자 정의 헤더 액션이 있으면 사용, 없으면 기본 액션 사용
  const finalHeaderActions = headerActions || defaultHeaderActions;

  // Default sidebar sections if none provided
  const defaultSidebarSections = [
    {
      id: 'user-section',
      title: '이용자',
      items: [
        {
          id: 'dashboard',
          label: '대시보드',
          icon: <Icon name="Home" size={20} />,
          href: '/dashboard',
        },
        {
          id: 'notices',
          label: '특이사항',
          icon: <Icon name="AlertTriangle" size={20} />,
          href: '/notices',
          badge: todayNoticesCount > 0 ? todayNoticesCount.toString() : undefined,
        },
        {
          id: 'schedule',
          label: '일정 관리',
          icon: <Icon name="Today" size={20} />,
          href: '/schedule',
          badge: todaySchedulesCount > 0 ? todaySchedulesCount.toString() : undefined,
        },
        {
          id: 'users',
          label: '정보 관리',
          icon: <Icon name="Person" size={20} />,
          href: '/users',
        },

        {
          id: 'purchase-history',
          label: '구매 내역',
          icon: <Icon name="CreditCard" size={20} />,
          href: '/purchase-history',
        },

        {
          id: 'individualized-services',
          label: '개별화서비스',
          icon: <Icon name="Target" size={20} />,
          href: '/individualized-services',
        },
        {
          id: 'ai-analysis',
          label: 'AI 분석',
          icon: <Icon name="Brain" size={20} />,
          href: '/ai-analysis',
        },
      ]
    },
    {
      id: 'staff-section',
      title: '종사자',
      items: [
        {
          id: 'announcements',
          label: '공지사항',
          icon: <Icon name="Edit" size={20} />,
          href: '/announcements',
        },
        {
          id: 'work-schedule',
          label: '근무 편성표',
          icon: <Icon name="Schedule2" size={20} />,
          href: '/work-schedule',
        },
        {
          id: 'leave-management',
          label: '연가현황',
          icon: <Icon name="BarChart3" size={20} />,
          href: '/leave-management',
        },
        {
          id: 'vehicles',
          label: '차량·출장 관리',
          icon: <Icon name="DirectionsCar" size={20} />,
          href: '/vehicles',
        },
        {
          id: 'staff-management',
          label: '근무 관리',
          icon: <Icon name="ClipboardList" size={20} />,
          href: '/staff',
        },
        {
          id: 'employees',
          label: '종사자 관리',
          icon: <Icon name="Groups" size={20} />,
          href: '/employees',
        },
      ]
    },
    {
      id: 'system-section',
      title: '시스템',
      items: [
        {
          id: 'settings',
          label: '설정',
          icon: <Icon name="Settings" size={20} />,
          href: '/settings',
        },
      ]
    }
  ];


  // 관리자 전용 메뉴 필터링
  const filteredSidebarSections = defaultSidebarSections.map(section => ({
    ...section,
    items: section.items.filter(item => {
      const adminOnlyIds = [
        'announcements',
        'ai-analysis',
        'employees',
        'staff-management',
        'settings'
      ];
      return isAdmin || !adminOnlyIds.includes(item.id);
    })
  })).filter(section => section.items.length > 0);

  const sectionsToRender = sidebarSections.length > 0 ? sidebarSections : filteredSidebarSections;

  return (
    <div className={clsx('min-h-screen bg-background relative overflow-x-hidden selection:bg-primary/20', className)}>
      {/* High-Tech Ambient Background Atmosphere */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 print:hidden" aria-hidden="true">
        {/* Subtle Tech Grid Texture */}
        <div className="absolute inset-0 bg-tech-grid opacity-70 dark:opacity-40" />
        
        {/* Luminous Ambient Glow Orbs */}
        <div className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full bg-indigo-500/12 dark:bg-indigo-500/18 blur-[130px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute top-1/4 -right-20 w-[500px] h-[500px] rounded-full bg-cyan-500/10 dark:bg-cyan-500/12 blur-[120px]" />
        <div className="absolute -bottom-32 left-1/3 w-[650px] h-[650px] rounded-full bg-purple-500/8 dark:bg-violet-600/14 blur-[140px]" />
      </div>

      {/* Header (모바일에서는 숨기고 하단 탭바만 사용) */}
      <div className="hidden lg:block relative z-40 print:hidden">
        <Header actions={finalHeaderActions} />
      </div>

      <div className="flex relative z-10">
        {/* Sidebar (데스크톱 고정 레일) */}
        <div className="print:hidden">
          <Sidebar
            sections={sectionsToRender}
            collapsed={isCollapsed}
            onToggleCollapse={handleToggleCollapse}
          />
        </div>

        {/* Main Content */}
        <main
          className={clsx(
            'flex-1 min-h-screen',
            'lg:min-h-[calc(100vh-4rem)]',
            'transition-all duration-300 ease-out',
            'overflow-x-hidden',
            'w-full',
            'print:min-h-0 print:bg-white print:overflow-visible'
          )}
        >
          <div className={clsx('p-4 pb-28 sm:p-6 sm:pb-28 lg:p-8 mx-auto relative z-10', maxWidth, 'print:p-0 print:m-0 print:max-w-none')}>
            {children}
          </div>
        </main>
      </div>

      {/* 모바일 하단 탭바 */}
      <MobileBottomNav sections={sectionsToRender} onLogout={handleLogout} />
    </div>
  );
};